import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import './AdminHomePage.css';

const AdminHomePage = () => {
    const navigate = useNavigate();
    
    const [paperStats, setPaperStats] = useState({
        total: 0,
        oral: 0,
        poster: 0,
        loading: true,
        error: null
    });

    const [isDownloading, setIsDownloading] = useState(false);

    // Navigation handlers for quick actions
    const handleNavigation = (path) => {
        navigate(path);
    };

    // Function to download papers data as Excel
    const handleDownloadExcel = async () => {
        setIsDownloading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/submit/get-papers`
            );

            if (!response.data?.data || response.data.data.length === 0) {
                Swal.fire({
                    icon: 'info',
                    title: 'No Data',
                    text: 'No papers found to export',
                    confirmButtonColor: '#5e71f6'
                });
                setIsDownloading(false);
                return;
            }

            const papers = response.data.data;

            // Dynamically import xlsx to reduce bundle size
            const XLSX = await import('xlsx');

            // Prepare data for Excel
            const excelData = papers.map((paper, index) => {
                // Parse domains
                let mainDomain = 'N/A';
                let subDomain1 = 'N/A';
                let subDomain2 = 'N/A';

                try {
                    let domains = [];
                    if (Array.isArray(paper.paperDomains)) {
                        if (typeof paper.paperDomains[0] === 'string' && paper.paperDomains[0].startsWith('[')) {
                            domains = JSON.parse(paper.paperDomains[0]);
                        } else {
                            domains = paper.paperDomains;
                        }
                    } else if (typeof paper.paperDomains === 'string') {
                        domains = JSON.parse(paper.paperDomains);
                    }

                    // Check if domains contain "|" separator (new format with main|sub)
                    const hasSubdomainSeparator = domains[0]?.includes('|');
                    
                    if (hasSubdomainSeparator) {
                        mainDomain = domains[0]?.split('|')[0]?.trim() || 'N/A';
                        subDomain1 = domains[0]?.split('|')[1]?.trim() || 'N/A';
                        subDomain2 = domains[1]?.split('|')[1]?.trim() || 'N/A';
                    } else {
                        mainDomain = domains[0] || 'N/A';
                        subDomain1 = domains[1] || 'N/A';
                        subDomain2 = domains[2] || 'N/A';
                    }
                } catch (e) {
                    console.error('Error parsing domains:', e);
                }

                // Get all authors' names and emails
                const authorNames = paper.author?.map(a => `${a.firstName} ${a.lastName}`).join('; ') || 'N/A';
                const authorEmails = paper.author?.map(a => a.email).join('; ') || 'N/A';

                return {
                    'Paper ID': paper.paperID || index + 1,
                    'Title': paper.title || 'N/A',
                    'Authors': authorNames,
                    'Emails': authorEmails,
                    'Keywords': paper.keywords || 'N/A',
                    'Abstract': paper.abstract || 'N/A',
                    'Main Domain': mainDomain,
                    'Sub Domain 1': subDomain1,
                    'Sub Domain 2': subDomain2,
                    'Presentation Type': paper.presentationType || 'N/A',
                    'Status': paper.status || 'Pending',
                    'Submission Date': paper.createdAt ? new Date(paper.createdAt).toLocaleDateString() : 'N/A'
                };
            });

            // Create worksheet
            const worksheet = XLSX.utils.json_to_sheet(excelData);

            // Set column widths
            worksheet['!cols'] = [
                { wch: 10 },  // Paper ID
                { wch: 50 },  // Title
                { wch: 40 },  // Authors
                { wch: 40 },  // Emails
                { wch: 30 },  // Keywords
                { wch: 80 },  // Abstract
                { wch: 25 },  // Main Domain
                { wch: 25 },  // Sub Domain 1
                { wch: 25 },  // Sub Domain 2
                { wch: 18 },  // Presentation Type
                { wch: 12 },  // Status
                { wch: 15 }   // Submission Date
            ];

            // Create workbook
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Papers Data');

            // Generate filename with current date
            const date = new Date().toISOString().split('T')[0];
            const filename = `Conference_Papers_Data_${date}.xlsx`;

            // Download file
            XLSX.writeFile(workbook, filename);

            Swal.fire({
                icon: 'success',
                title: 'Download Successful!',
                text: `Downloaded ${papers.length} papers data as ${filename}`,
                confirmButtonColor: '#5e71f6'
            });

        } catch (error) {
            console.error('Error downloading Excel:', error);
            Swal.fire({
                icon: 'error',
                title: 'Download Failed',
                text: 'Failed to download papers data. Please try again.',
                confirmButtonColor: '#5e71f6'
            });
        } finally {
            setIsDownloading(false);
        }
    };

    // Static important dates - can be made dynamic later
    const importantDates = [
        {
            label: "Abstract Submission Deadline",
            date: "7th December",
            year: "2025",
            status: "active",
            icon: "📝"
        },
        {
            label: "Notification of Acceptance",
            date: "14th December", 
            year: "2025",
            status: "upcoming",
            icon: "📧"
        },
        {
            label: "Registration Deadline",
            date: "23th December",
            year: "2025",
            status: "upcoming", 
            icon: "💳"
        },
        {
            label: "Conference Dates",
            date: "15 May",
            year: "2026",
            status: "upcoming",
            icon: "🎯"
        }
    ];

    useEffect(() => {
        const fetchPaperStats = async () => {
            try {
                setPaperStats(prev => ({ ...prev, loading: true, error: null }));
                
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/submit/get-papers`
                );

                if (response.data?.data) {
                    const papers = response.data.data;
                    const total = papers.length;
                    const oral = papers.filter(paper => paper.presentationType === 'Oral').length;
                    const poster = papers.filter(paper => paper.presentationType === 'Poster').length;

                    setPaperStats({
                        total,
                        oral,
                        poster,
                        loading: false,
                        error: null
                    });
                } else {
                    setPaperStats(prev => ({
                        ...prev,
                        loading: false,
                        error: 'Unexpected data format received from server'
                    }));
                }
            } catch (error) {
                console.error('Error fetching paper statistics:', error);
                setPaperStats(prev => ({
                    ...prev,
                    loading: false,
                    error: 'Failed to load paper statistics'
                }));
            }
        };

        fetchPaperStats();
    }, []);

    return (
        <div className="admin-home-wrapper">
            <div className="admin-home-container">
                {/* Header Section */}
                <div className="header-section">
                    <h1 className="page-title">Admin Dashboard</h1>
                    <p className="page-subtitle">
                        Welcome to the Conference Management System - Overview and Key Statistics
                    </p>
                </div>

                {/* Paper Statistics Cards */}
                <div className="stats-section">
                    <div className="section-header">
                        <h2 className="section-title">Paper Submission Statistics</h2>
                        <button 
                            className="download-excel-btn"
                            onClick={handleDownloadExcel}
                            disabled={isDownloading || paperStats.loading}
                        >
                            {isDownloading ? (
                                <>
                                    <span className="btn-spinner"></span>
                                    <span>Downloading...</span>
                                </>
                            ) : (
                                <>
                                    <svg 
                                        className="btn-icon" 
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path 
                                            strokeLinecap="round" 
                                            strokeLinejoin="round" 
                                            strokeWidth={2} 
                                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                                        />
                                    </svg>
                                    <span>Download Excel</span>
                                </>
                            )}
                        </button>
                    </div>
                    <div className="stats-grid">
                        <div className="stat-card total-papers">
                            <div className="stat-icon">📊</div>
                            <div className="stat-content">
                                <div className="stat-number">
                                    {paperStats.loading ? (
                                        <div className="loading-spinner-small"></div>
                                    ) : paperStats.error ? (
                                        <span className="error-text">--</span>
                                    ) : (
                                        paperStats.total
                                    )}
                                </div>
                                <div className="stat-label">Total Papers</div>
                                <div className="stat-description">All submitted papers</div>
                            </div>
                        </div>

                        <div className="stat-card oral-papers">
                            <div className="stat-icon">🎤</div>
                            <div className="stat-content">
                                <div className="stat-number">
                                    {paperStats.loading ? (
                                        <div className="loading-spinner-small"></div>
                                    ) : paperStats.error ? (
                                        <span className="error-text">--</span>
                                    ) : (
                                        paperStats.oral
                                    )}
                                </div>
                                <div className="stat-label">Oral Presentations</div>
                                <div className="stat-description">Oral presentation papers</div>
                            </div>
                        </div>

                        <div className="stat-card poster-papers">
                            <div className="stat-icon">🖼️</div>
                            <div className="stat-content">
                                <div className="stat-number">
                                    {paperStats.loading ? (
                                        <div className="loading-spinner-small"></div>
                                    ) : paperStats.error ? (
                                        <span className="error-text">--</span>
                                    ) : (
                                        paperStats.poster
                                    )}
                                </div>
                                <div className="stat-label">Poster Presentations</div>
                                <div className="stat-description">Poster presentation papers</div>
                            </div>
                        </div>
                    </div>
                    
                    {paperStats.error && (
                        <div className="error-message">
                            <span className="error-icon">⚠️</span>
                            {paperStats.error}
                            <button 
                                className="retry-btn"
                                onClick={() => window.location.reload()}
                            >
                                Retry
                            </button>
                        </div>
                    )}
                </div>

                {/* Important Dates Section */}
                <div className="dates-section">
                    <h2 className="section-title">Important Dates</h2>
                    <div className="dates-timeline">
                        {importantDates.map((dateItem, index) => (
                            <div 
                                key={index} 
                                className={`date-item ${dateItem.status}`}
                                style={{ zIndex: importantDates.length - index }}
                            >
                                <div className="date-content">
                                    <span className="date-icon">{dateItem.icon}</span>
                                    <span className="date-text">
                                        <strong>{dateItem.label}</strong> - {dateItem.date}, {dateItem.year}
                                    </span>
                                    <span className={`date-badge ${dateItem.status}`}>
                                        {dateItem.status === 'active' ? 'Active' : 'Upcoming'}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Quick Actions Section */}
                <div className="quick-actions-section">
                    <div className="quick-actions-card">
                        <div className="card-header">
                            <h3 className="card-title">
                                <span className="title-icon">⚡</span>
                                Quick Actions
                            </h3>
                        </div>
                        <div className="card-body">
                            <div className="actions-grid">
                                <button 
                                    className="action-btn papers-btn"
                                    onClick={() => handleNavigation('/admin/customize-paper')}
                                >
                                    <span className="action-icon">📄</span>
                                    <span className="action-label">View All Papers</span>
                                    <span className="action-description">Review submitted papers</span>
                                </button>
                                
                                <button 
                                    className="action-btn schedule-btn"
                                    onClick={() => handleNavigation('/admin/add-schedule')}
                                >
                                    <span className="action-icon">📅</span>
                                    <span className="action-label">Manage Schedule</span>
                                    <span className="action-description">Create session schedules</span>
                                </button>
                                
                                <button 
                                    className="action-btn settings-btn"
                                    onClick={() => handleNavigation('/admin/customize-date')}
                                >
                                    <span className="action-icon">📅</span>
                                    <span className="action-label">Conference Dates</span>
                                    <span className="action-description">Update conference dates</span>
                                </button>
                                
                                <button 
                                    className="action-btn reports-btn"
                                    onClick={() => handleNavigation('/admin/print-paper-summary')}
                                >
                                    <span className="action-icon">📊</span>
                                    <span className="action-label">Generate Reports</span>
                                    <span className="action-description">Download summaries</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminHomePage;